import { z } from 'zod';

import { CompanyId, LosId } from '../BrandedIds';
import { PaymentFrequency } from '../loan/Loan';
import { LoanStatus } from '../loan/LoanStatus';

export type BorrowerEmailHtmlData =
  | GoodbyeEmailData
  | GoodbyeBeforeWelcomeEmailData
  | PaymentMadeEmailData
  | PaymentDueEmailData
  | PaymentDueAutopayEmailData
  | FinalPaymentDueEmailData
  | PaymentReminderEmailData
  | PaymentReminderAutopayEmailData
  | RescindGoodbyeEmailData
  | WelcomeEmailData
  | WelcomePreActiveEmailData
  | BorrowerEmailData
  | PaidInFullStatementEmailData
  | PartialPayoffConfirmationEmailData
  | Form1098AvailableEmailData
  | EscrowAnalysisAvailableEmailData
  | LoanIsMaturingEmailData
  | InterestRateAdjustmentEmailData;

// --- BEGIN: Customizable email templates. DO NOT REMOVE FIELDS --- //
export interface MailingLetterData {
  mailingName: string;
  mailingAddressLine1?: string;
  mailingAddressLine2?: string;
}

// All email data should extend this interface
export interface BaseEmailData {
  lenderId: CompanyId;
  lenderName: string;
  lenderNumber: string;
  lenderEmail: string;
  name: string; // first name
  lastName: string;
  fullName: string;
  losId: LosId;
  maskedLosId: string; // last 4 digits (x1234)
  propertyAddressLine1: string;
  propertyAddressLine2: string;
  entityName?: string;
  allBorrowerNames: string[];
  propertyAddresses: string[]; // single-line addresses of all active loan properties
}

export interface BorrowerEmailData extends BaseEmailData {
  portalUrl: string;
}

export interface BorrowerInsuranceExpirationEmailData extends BorrowerEmailData {
  policyType: string; // string, not EscrowInsuranceType, to allow fallback to insuranceName or `'n/a'`
  policyNumber: string;
  policyEndDate: string;
}

export interface GoodbyeEmailData extends MailingLetterData, BorrowerEmailData {
  loanOfficerName?: string;
  loanOfficerEmail?: string;
  poolId?: string;
  lenderAddress: string;
  transferEffectiveDate: string;
  transferSoldDate?: string;
  newLenderName: string;
  newLoanNumber: string;
  newLenderCorrespondenceAddress?: string;
  newLenderPaymentAddress: string;
  newLenderPhone: string;
  statementDate: string;
  paymentDueAmount: string;
  paymentDueAmountFromCredit?: string;
  paymentDueDate: string;
  interestReserveBalance?: string;
  remainingBalanceForBorrower?: string;
  newServicerWebsite?: string;
  newServicerHours1?: string;
  newServicerHours2?: string;
  newServicerHours3?: string;
  isPaymentLate: boolean;
  isUpdate: boolean;
  propertyAddressCity: string;
  propertyAddressState: string;
  propertyAddressZip: string;
}

export interface GoodbyeBeforeWelcomeEmailData extends MailingLetterData, BorrowerEmailData {
  lenderAddress: string;
  transferEffectiveDate: string;
  transferSoldDate?: string;
  newLenderName: string;
  newLoanNumber: string;
  newLenderCorrespondenceAddress?: string;
  newLenderPaymentAddress: string;
  newLenderPhone: string;
  loanOfficerName?: string;
  loanOfficerEmail?: string;
  poolId?: string;
  newServicerWebsite?: string;
  newServicerHours1?: string;
  newServicerHours2?: string;
  newServicerHours3?: string;
  isUpdate: boolean;
  propertyAddressCity: string;
  propertyAddressState: string;
  propertyAddressZip: string;
}

export interface WelcomeEmailData extends BorrowerEmailData {
  paymentDueDate: string;
  paymentDueAmount: string;
  paymentDueAmountFromCredit?: string;
  appUrl: string;
  setPasswordUrl: string;
  propertyAddress: string;
  paymentAddress?: string;
  interestReserveBalance?: string;
  remainingBalanceForBorrower?: string;
  autopayFirstPaymentDate?: string;
  incomingTransferEffectiveDate?: string;
  paymentFrequency: PaymentFrequency;
  previousLenderName?: string;
  previousLenderPhone?: string;
}

export interface WelcomePreActiveEmailData extends WelcomeEmailData {
  statementDate: string;
  lenderMailingAddress: string | undefined;
  previousLenderName?: string;
  previousLenderPhone?: string;
  purchaseDate?: string;
  productDescription: string;
  unpaidPrincipalBalance: string;
}

export interface PaymentDueEmailData extends MailingLetterData, BorrowerEmailData {
  paymentDueDate: string;
  paymentDueAmount: string;
  paymentDueAmountFromCredit?: string;
  propertyAddress: string;
  interestReserveBalance?: string;
  remainingBalanceForBorrower?: string;
  autopayDayOfMonth?: string;
}

export interface FinalPaymentDueEmailData extends MailingLetterData, BorrowerEmailData {
  paymentDueDate: string;
}

export interface PortalLoanEmailData extends MailingLetterData, BorrowerEmailData {}

export interface AutopayEmailData extends PortalLoanEmailData {
  autopayBankAccountIdentifier: string;
  autopayType: 'dwolla' | 'usio';
  dayOfTheMonth: string | undefined;
  autopayDayOfMonth: string | undefined;
}

export interface PaymentDueAutopayEmailData extends PaymentDueEmailData {
  autopayBankAccountIdentifier: string;
  autopayType: 'dwolla' | 'usio';
  dayOfTheMonth: string | undefined;
}
export interface PaymentMadeEmailData extends PaymentDueEmailData {
  paymentAmount: string;
  paymentDate: string;
  bankAccountIdentifier: string;
  paymentMethod: string;
}

export interface PaymentReminderEmailData extends PaymentDueEmailData {
  paymentDueMonth: string;
}

export interface PaymentReminderAutopayEmailData extends PaymentDueAutopayEmailData {
  paymentDueMonth: string;
  dayOfTheMonth: string | undefined;
}

export interface PaymentFailedEmailData extends PaymentMadeEmailData {
  failureCode: string | undefined;
  achType: 'usio' | 'dwolla';
}

export const UsioFailureReason = z.enum(['transactionLimit', 'badAccount', 'duplicate', 'generic']);
export type UsioFailureReason = z.infer<typeof UsioFailureReason>;

export interface PaymentFailedAutopayEmailData extends PaymentDueEmailData {
  failureCode: string | undefined;
  failureReason: UsioFailureReason | undefined; // in case of failure before payment initiation
  autopayType: 'usio' | 'dwolla';
}

export interface RescindGoodbyeEmailData extends PaymentDueEmailData, MailingLetterData {
  transferEffectiveDate: string;
  newLenderName: string;
  newLoanStatus: LoanStatus;
}

export interface PaidInFullStatementEmailData extends BorrowerEmailData {
  payoffDate: string;
  hasEscrow: boolean;
  hasRefundDue: boolean;
  isUpdate: boolean;
}

export interface PartialPayoffConfirmationEmailData extends BorrowerEmailData {
  payoffDate: string;
  payoffAmount: string;
  properties: string[]; // properties specific to being paid off
}

export interface Form1098AvailableEmailData extends BorrowerEmailData, MailingLetterData {
  year: string;
  isCorrected: boolean;
}

export interface EscrowAnalysisAvailableEmailData extends BorrowerEmailData, MailingLetterData {
  effectiveDate: string;
  isCorrected: boolean;
}

export interface DefermentCancelledEmailData extends BorrowerEmailData {
  totalAmount: string;
}

export interface DelinquentInterestEmailData extends BorrowerEmailData {
  delinquentInterestRate: string;
  effectiveDate: string;
}

export interface PrincipalModificationAppliedEmailData extends BorrowerEmailData {
  principalModificationEffectiveDate: string;
  principalModificationAmount: string;
  principalModificationIncrease: boolean;
  outstandingBalance: string;
}

export interface LoanIsMaturingEmailData extends BorrowerEmailData {
  maturityDate: string;
}

export interface InterestRateAdjustmentEmailData extends BorrowerEmailData {
  isFirstNotification: boolean;
  rateChangeDate: string; // mm/dd/yyyy
  rateChangeDateLong: string; // e.g. "June 1, 2026"
  fixedPeriodLabel?: string; // e.g. "5 year" / "18 month", fills "you had a <x> period"
  adjustmentInterval: string; // e.g. "1 year" / "6 months", fills "may change every <x>"
  currentInterestRate: string;
  currentTotalPayment: string;
  newInterestRate: string; // estimated when isFirstNotification, locked in otherwise
  newTotalPayment: string;
}

// --- END: Customizable email templates. Do not remove fields --- //
